import { authenticatedClient } from '../lib/client';
import { RUNS_ENDPOINTS } from './runs.utils';
import type { ApiResponse } from '../lib/types';
import type { SearchRunsRequest, SearchRunsResponse } from './runs.types';

/**
 * Search Activepieces flow runs with filters.
 * 
 * Security: The organizationId is automatically extracted from the authenticated 
 * user's token and applied to all searches. Users can only see runs from their 
 * own organization.
 * 
 * @param params - Search parameters
 * @returns Paginated list of runs matching the search criteria
 */
export const searchRuns = async (
    params: SearchRunsRequest = {}
): Promise<ApiResponse<SearchRunsResponse>> => {
    try {
        // Build request body with defaults
        const requestBody: SearchRunsRequest = {
            query: params.query,
            searchMode: params.searchMode || 'phrase',
            projectId: params.projectId,
            flowId: params.flowId,
            status: params.status,
            hasFailedSteps: params.hasFailedSteps,
            startDate: params.startDate,
            endDate: params.endDate,
            minDurationMs: params.minDurationMs,
            maxDurationMs: params.maxDurationMs,
            page: params.page || 1,
            pageSize: params.pageSize || 20,
            sortBy: params.sortBy || 'start_time',
            sortOrder: params.sortOrder || 'desc',
        };

        // Remove undefined values
        const cleanedBody = Object.fromEntries(
            Object.entries(requestBody).filter(([, value]) => value !== undefined)
        );

        const response = await authenticatedClient.post<SearchRunsResponse>(
            RUNS_ENDPOINTS.SEARCH,
            cleanedBody
        );

        return { data: response.data };
    } catch (error: any) {
        // Handle validation errors (422)
        if (error.response?.status === 422) {
            const validationErrors = error.response?.data?.detail;
            if (Array.isArray(validationErrors) && validationErrors.length > 0) {
                const firstError = validationErrors[0];
                return { error: `Validation error: ${firstError.msg}` };
            }
        }

        // Handle generic errors
        const errorDetail = error.response?.data?.detail || '';
        const errorMessage = typeof errorDetail === 'string' 
            ? errorDetail
                .replace(/status_code: \d+, body: /, '')
                .replace(/[{}'\"]/g, '')
                .replace(/message: /, '')
                .replace(/, error: .*$/, '')
                .trim()
            : '';

        return { error: errorMessage || 'Failed to search runs' };
    }
};
